/* HC-EPIC-002 · Sprint 1B-B · US 1.9-A / US 1.9-B · Denied-Route Experience.
 *
 * US 1.9-A: authenticated-only page shown when a user's frontend permission
 * or module guard denies a route. Registered under the existing `<Wrapped>`
 * authentication-only pattern (App.js) so that this route:
 *   - requires an authenticated user;
 *   - is independent of permission resolution;
 *   - remains reachable when the user's permission set is empty or
 *     unavailable;
 *   - never invokes the permission-denied branch of ProtectedRoute;
 *   - never redirects to itself;
 *   - performs no automatic API request on mount for the denied variant.
 *
 * US 1.9-B: renders a distinct "resolver_failure" variant when the guard
 * observed a resolver-level failure (network / 5xx / 429 / 401 / 403 on
 * /permissions/me). The retryable classes (network / 5xx / 429) expose a
 * "Retry access check" control that dispatches refreshPermissions({force:true}).
 * The non-retryable classes (401 / 403) preserve fail-closed behavior and
 * hide the retry control. On successful retry the page navigates `replace`
 * back to the original protected route if that target is safe.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { BRAND } from "../lib/brandingConfig";

// Safe return-target validator. Only truly-internal, non-recovery paths pass.
// Private module-scope helper — not exported. Callers exercise this validator
// exclusively through observable component navigation behavior in tests.
function isSafeReturnTarget(from) {
  if (typeof from !== "string") return false;
  if (from.length === 0) return false;
  if (from[0] !== "/") return false;
  if (from.startsWith("//")) return false;
  if (from.startsWith("/\\")) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(from)) return false;
  if (from === "/access-denied"
      || from.startsWith("/access-denied?")
      || from.startsWith("/access-denied#")
      || from.startsWith("/access-denied/")) return false;
  if (from === "/login"
      || from.startsWith("/login?")
      || from.startsWith("/login#")
      || from.startsWith("/login/")) return false;
  return true;
}

export default function AccessDenied() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const {
    user,
    logout,
    permissionState,
    permissionResolverError,
    permissionRetryAt,
    permissionsFresh,
    refreshPermissions,
  } = auth;

  const rawState = location.state && typeof location.state === "object" ? location.state : null;
  const from = typeof rawState?.from === "string" && rawState.from.length > 0 ? rawState.from : null;
  const requirePermission =
    typeof rawState?.requirePermission === "string" && rawState.requirePermission.length > 0
      ? rawState.requirePermission
      : null;
  const requireModule =
    typeof rawState?.requireModule === "string" && rawState.requireModule.length > 0
      ? rawState.requireModule
      : null;
  const initialVariant =
    rawState?.variant === "resolver_failure" ? "resolver_failure" : "denied";
  const initialResolverErrorFromRouter =
    typeof rawState?.resolverError === "string" ? rawState.resolverError : null;

  // Live AuthContext is the authoritative live source. Router state is used
  // only for initial variant + `from` + initial resolverError seed.
  const liveResolverError =
    typeof permissionResolverError === "string" ? permissionResolverError : null;
  const effectiveResolverError = liveResolverError || initialResolverErrorFromRouter;

  // Genuine denial only when the initial redirect specified the denied
  // variant AND the resolver has no live error. Otherwise we're in the
  // resolver-failure experience.
  const isResolverFailureVariant =
    initialVariant === "resolver_failure" || !!liveResolverError;

  const supportEmail = BRAND && typeof BRAND.supportEmail === "string" ? BRAND.supportEmail : "";
  const supportSubject = "HarborWell Connect — Access denied";
  const supportBodyLines = [
    "Hello HarborWell support,",
    "",
    "I received an access-denied message in HarborWell Connect and would like assistance.",
  ];
  if (from) supportBodyLines.push("", `Route: ${from}`);
  const mailtoHref = supportEmail
    ? `mailto:${supportEmail}?subject=${encodeURIComponent(supportSubject)}&body=${encodeURIComponent(supportBodyLines.join("\n"))}`
    : null;

  const hasDenialSupportContext = Boolean(from || requirePermission || requireModule);
  const role = user && typeof user.role === "string" ? user.role : null;

  // Cooldown countdown (only for retryable resolver-failure variants with a
  // live future retryAt). Never created for denial / 401 / 403 / past retry.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!isResolverFailureVariant) return undefined;
    if (effectiveResolverError === "401" || effectiveResolverError === "403") return undefined;
    const retryAt = Number.isFinite(permissionRetryAt) ? permissionRetryAt : 0;
    if (!(retryAt > Date.now())) return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      setNowTick(now);
      if (now >= retryAt) {
        try { clearInterval(id); } catch { /* noop */ }
      }
    }, 500);
    return () => { try { clearInterval(id); } catch { /* noop */ } };
  }, [isResolverFailureVariant, effectiveResolverError, permissionRetryAt]);

  const cooldownActive = useMemo(() => {
    const retryAt = Number.isFinite(permissionRetryAt) ? permissionRetryAt : 0;
    return retryAt > 0 && nowTick < retryAt;
  }, [permissionRetryAt, nowTick]);

  const cooldownSecondsRemaining = useMemo(() => {
    if (!cooldownActive) return 0;
    return Math.max(0, Math.ceil((permissionRetryAt - nowTick) / 1000));
  }, [cooldownActive, permissionRetryAt, nowTick]);

  const retryEligible =
    isResolverFailureVariant &&
    effectiveResolverError !== "401" &&
    effectiveResolverError !== "403";

  // Successful retry auto-navigation. Only fires once per mount. Uses live
  // AuthContext state (not router state) as the source of truth.
  const navigatedRef = useRef(false);
  useEffect(() => {
    if (!isResolverFailureVariant) return;
    if (navigatedRef.current) return;
    if (permissionState === "ready" && !liveResolverError) {
      if (from && isSafeReturnTarget(from)) {
        navigatedRef.current = true;
        try { navigate(from, { replace: true }); } catch { /* noop */ }
      }
    }
  }, [isResolverFailureVariant, permissionState, liveResolverError, from, navigate]);

  const handleReturnToAccount = () => { navigate("/my-account"); };
  const handleSignOut = async () => {
    try {
      if (typeof logout === "function") { await logout(); }
    } finally {
      navigate("/login");
    }
  };
  const handleRetry = () => {
    if (!retryEligible) return;
    if (cooldownActive) return;
    if (typeof refreshPermissions === "function") {
      void refreshPermissions({ force: true });
    }
  };

  // ---------- Copy per variant ----------
  let headingText = "Access denied";
  let bodyText = "You don't have permission to view this page.";
  let headingTestId = "access-denied-heading";
  let bodyTestId = "access-denied-body";
  if (isResolverFailureVariant) {
    headingText = "Unable to verify access";
    if (effectiveResolverError === "401") {
      bodyText = "Your session could not be verified. Please sign in again.";
    } else if (effectiveResolverError === "403") {
      bodyText = "We couldn't verify your access. This does not necessarily mean you lack permission for this page — access resolution itself failed. Please try again from your account, or contact HarborWell support.";
    } else {
      bodyText = "We couldn't verify your access. This usually clears in a moment. You can retry, or return to your account and try again shortly.";
    }
  }

  return (
    <main
      data-testid="access-denied-page"
      data-variant={isResolverFailureVariant ? "resolver_failure" : "denied"}
      data-resolver-error={effectiveResolverError || ""}
      className="min-h-screen flex items-start justify-center bg-[#F9F8F6] px-4 py-16"
    >
      <div className="w-full max-w-2xl">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1 rounded-full bg-[#1E3A8A]/10 p-3" aria-hidden="true">
            <ShieldOff className="h-6 w-6 text-[#1E3A8A]" />
          </div>
          <div className="flex-1">
            <h1 data-testid={headingTestId} className="text-2xl sm:text-3xl font-semibold text-[#1E3A8A]">
              {headingText}
            </h1>
            <p data-testid={bodyTestId} className="mt-3 text-base text-slate-700">
              {bodyText}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {retryEligible ? (
                <button
                  type="button"
                  onClick={handleRetry}
                  disabled={cooldownActive}
                  data-testid="retry-access-check-btn"
                  className="inline-flex items-center justify-center rounded-md bg-[#1E3A8A] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#172d6d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {cooldownActive
                    ? `Retry available in ${cooldownSecondsRemaining}s`
                    : "Retry access check"}
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleReturnToAccount}
                data-testid="access-denied-return-account-btn"
                className="inline-flex items-center justify-center rounded-md bg-[#1E3A8A] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#172d6d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2 transition-colors"
              >
                Return to my account
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                data-testid="access-denied-signout-btn"
                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2 transition-colors"
              >
                Sign out and try a different account
              </button>
              {mailtoHref ? (
                <a
                  href={mailtoHref}
                  data-testid="access-denied-contact-support-link"
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2 transition-colors"
                >
                  Contact HarborWell support
                </a>
              ) : (
                <span
                  data-testid="access-denied-contact-support-text"
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-500"
                >
                  Contact HarborWell support
                </span>
              )}
            </div>

            {/* Support details.
              * - Denied variant: shows requirePermission / requireModule when
              *   supplied by the route guard (preserves US 1.9-A behavior).
              * - Resolver-failure variant: intentionally omits any
              *   requirePermission / requireModule claim (the resolver did
              *   not successfully resolve, so no claim about a specific
              *   missing permission is meaningful). Shows only the neutral
              *   route + role + resolver-error-class label. */}
            {!isResolverFailureVariant && hasDenialSupportContext ? (
              <details
                data-testid="access-denied-support-details"
                className="mt-10 rounded-md border border-slate-200 bg-white p-4 text-sm"
              >
                <summary
                  data-testid="access-denied-support-details-summary"
                  className="cursor-pointer font-medium text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2 rounded"
                >
                  Support details
                </summary>
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-y-2 gap-x-4 text-slate-700">
                  {from ? (
                    <>
                      <dt className="font-medium">Denied route</dt>
                      <dd data-testid="access-denied-support-from" className="sm:col-span-2 break-all">{from}</dd>
                    </>
                  ) : null}
                  {role ? (
                    <>
                      <dt className="font-medium">Your role</dt>
                      <dd data-testid="access-denied-support-role" className="sm:col-span-2 break-all">{role}</dd>
                    </>
                  ) : null}
                  {requirePermission ? (
                    <>
                      <dt className="font-medium">Required permission</dt>
                      <dd data-testid="access-denied-support-permission" className="sm:col-span-2 break-all">{requirePermission}</dd>
                    </>
                  ) : null}
                  {requireModule ? (
                    <>
                      <dt className="font-medium">Required module</dt>
                      <dd data-testid="access-denied-support-module" className="sm:col-span-2 break-all">{requireModule}</dd>
                    </>
                  ) : null}
                </dl>
              </details>
            ) : null}
            {isResolverFailureVariant && (from || role || effectiveResolverError) ? (
              <details
                data-testid="access-denied-resolver-support-details"
                className="mt-10 rounded-md border border-slate-200 bg-white p-4 text-sm"
              >
                <summary
                  className="cursor-pointer font-medium text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A] focus-visible:ring-offset-2 rounded"
                >
                  Support details
                </summary>
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-y-2 gap-x-4 text-slate-700">
                  {from ? (
                    <>
                      <dt className="font-medium">Route</dt>
                      <dd data-testid="access-denied-resolver-from" className="sm:col-span-2 break-all">{from}</dd>
                    </>
                  ) : null}
                  {role ? (
                    <>
                      <dt className="font-medium">Your role</dt>
                      <dd data-testid="access-denied-resolver-role" className="sm:col-span-2 break-all">{role}</dd>
                    </>
                  ) : null}
                  {effectiveResolverError ? (
                    <>
                      <dt className="font-medium">Reason</dt>
                      <dd data-testid="access-denied-resolver-reason" className="sm:col-span-2 break-all">
                        Access verification failed ({effectiveResolverError}).
                      </dd>
                    </>
                  ) : null}
                </dl>
              </details>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}
